import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getCartItemCount } from '../utils/cartUtils';
import { getProfileImageUrl } from '../utils/imageUtils';
import { FaShoppingCart, FaChartBar, FaUser, FaComments, FaDoorOpen, FaExclamationTriangle, FaMoneyBillWave, FaStar } from 'react-icons/fa';
import { HiOutlineShoppingCart } from 'react-icons/hi2';
import NotificationBell from './NotificationBell';
import RoleIllustration from './RoleIllustration';
import LanguageSwitcher from './LanguageSwitcher';
import { disconnectSocket } from '../utils/socket';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';
import './Header.css';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [imageKey, setImageKey] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    // Load user from localStorage
    const loadUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        setUser(null);
      }
    };

    loadUser();

    // Load cart count
    const updateCartCount = () => {
      setCartCount(getCartItemCount());
    };
    updateCartCount();

    // Listen for storage changes (when user logs in/out in another tab or profile picture changes)
    const handleStorageChange = (e) => {
      loadUser();
      // Force image refresh by updating imageKey
      setImageKey(prev => prev + 1);
      if (e.key === 'cart' || !e.key) {
        updateCartCount();
      }
    };

    // Listen for cart updates in same tab
    const handleCartUpdate = () => {
      updateCartCount();
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom storage events (for same-tab updates)
    window.addEventListener('userUpdated', handleStorageChange);
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    // Poll for cart updates (in case cart is updated without events)
    const cartInterval = setInterval(updateCartCount, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      clearInterval(cartInterval);
    };
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleLogout = async () => {
    try { await api.put('/users/logout'); } catch { /* ignore */ }
    disconnectSocket();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsProfileDropdownOpen(false);
    navigate('/');
  };

  // Get user's first name
  const getFirstName = (fullName) => {
    if (!fullName) return 'User';
    return fullName.split(' ')[0];
  };

  // First letter for avatar fallback when no profile image
  const getInitial = (fullName) => {
    if (!fullName || !fullName.trim()) return '?';
    return (fullName.trim()[0] || '?').toUpperCase();
  };

  const profileImageUrl = user?.profileImage
    ? getProfileImageUrl(user.profileImage, true)
    : null;

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        <div className="header-logo-group">
          <Link to="/" className="header-logo-container">
            <img src="/tajabazzarlogo.png" alt="Taja Bazzar Logo" className="header-logo-image" />
           
          </Link>
          <LanguageSwitcher />
        </div>

        <nav
          className={`header-nav ${isMenuOpen ? 'header-nav-open' : ''}`}
          onClick={(e) => {
            if (e.target.closest('a')) setIsMenuOpen(false);
          }}
        >
          {user?.role === 'farmer' ? (
            <>
              <Link to="/" className="header-nav-link">{t('header.home')}</Link>
              <Link to="/farmer/dashboard" className="header-nav-link">{t('header.dashboard')}</Link>
              <Link to="/farmer/products" className="header-nav-link">{t('header.products')}</Link>
              <Link to="/farmer/orders" className="header-nav-link">{t('header.orders')}</Link>
              <Link to="/farmer/payments" className="header-nav-link">{t('header.payments')}</Link>
              <Link to="/farmer/analytics" className="header-nav-link">{t('header.analytics')}</Link>
              <Link to="/nearby" className="header-nav-link">{t('header.nearby')}</Link>
            </>
          ) : user?.role === 'consumer' ? (
            <>
              <Link to="/consumer/dashboard" className="header-nav-link">{t('header.dashboard')}</Link>
              <Link to="/market" className="header-nav-link">{t('header.grocery')}</Link>
              <Link to="/nearby" className="header-nav-link">{t('header.nearby')}</Link>
              <Link to="/farmers" className="header-nav-link">{t('header.farmers')}</Link>
              <Link to="/orders" className="header-nav-link">{t('header.myOrders')}</Link>
            </>
          ) : (
            <>
              <Link to="/" className="header-nav-link">{t('header.home')}</Link>
              <Link to="/market" className="header-nav-link">{t('header.grocery')}</Link>
              <Link to="/nearby" className="header-nav-link">{t('header.nearby')}</Link>
              <Link to="/farmers" className="header-nav-link">{t('header.farmers')}</Link>
              <Link to="/guides" className="header-nav-link">{t('header.guides')}</Link>
              <Link to="/about" className="header-nav-link">{t('header.about')}</Link>
              <Link to="/contact" className="header-nav-link">{t('header.contact')}</Link>
              <Link to="/faq" className="header-nav-link">{t('header.faq')}</Link>
            </>
          )}
        </nav>

        <div className="header-actions">
          {user ? (
            <>
              {user.role === 'consumer' && (
                <button className="header-cart-btn" onClick={() => navigate('/cart')} aria-label="Shopping Cart">
                  {cartCount > 0 ? <FaShoppingCart className="header-cart-icon" /> : <HiOutlineShoppingCart className="header-cart-icon header-cart-icon-empty" />}
                  {cartCount > 0 && <span className="header-cart-badge">{cartCount}</span>}
                </button>
              )}

              <NotificationBell />

              <div className="header-profile-container" ref={dropdownRef}>
                <button
                  className="header-profile-btn"
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  aria-label="User Menu"
                >
                  <span className="header-user-name">{getFirstName(user.name)}</span>
                  <div className="header-profile-avatar">
                    {profileImageUrl ? (
                      <img
                        key={`profile-img-${imageKey}`}
                        src={profileImageUrl}
                        alt={user.name}
                        className="header-profile-image"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const fallback = e.target.nextElementSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span className="header-profile-initial" style={{ display: profileImageUrl ? 'none' : 'flex' }} aria-hidden="true">{getInitial(user.name)}</span>
                  </div>
                </button>

                {isProfileDropdownOpen && (
                  <div className="header-dropdown">
                    <div className="header-dropdown-header">
                      <div className="header-dropdown-avatar">
                        {profileImageUrl ? (
                          <img
                            key={`dropdown-img-${imageKey}`}
                            src={profileImageUrl}
                            alt={user.name}
                            className="header-dropdown-profile-image"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span className="header-dropdown-profile-initial" style={{ display: profileImageUrl ? 'none' : 'flex' }} aria-hidden="true">{getInitial(user.name)}</span>
                      </div>
                      <div className="header-dropdown-user-info">
                        <div className="header-dropdown-user-name">{user.name}</div>
                        <div className="header-dropdown-user-email">{user.email}</div>
                        <span className={`header-dropdown-role header-dropdown-role-${user.role}`}>
                          {user.role === 'farmer' && <RoleIllustration role="farmer" size="sm" className="header-dropdown-role-fig" />}
                          {user.role === 'consumer' && (
                            <RoleIllustration
                              role="consumer"
                              size="sm"
                              className="header-dropdown-role-fig"
                              strictConsumerGender
                              gender={user.gender}
                            />
                          )}
                          {user.role === 'admin' ? t('common.admin') : user.role === 'farmer' ? t('common.farmer') : t('common.consumer')}
                        </span>
                      </div>
                    </div>
                    <div className="header-dropdown-divider" />
                    {user.role !== 'consumer' && (
                      <Link to={`/${user.role}/dashboard`} className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaChartBar className="header-dropdown-icon" /> {t('header.dashboard')}
                      </Link>
                    )}
                    {user.role !== 'admin' && (
                      <Link to={user.role === 'farmer' ? '/farmer/profile' : `/${user.role}/profile`} className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaUser className="header-dropdown-icon" /> {t('header.myProfile')}
                      </Link>
                    )}
                    {user.role !== 'admin' && (
                      <Link to="/messages" className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaComments className="header-dropdown-icon" /> {t('header.messages')}
                      </Link>
                    )}
                   
                    {user.role === 'consumer' && (
                      <>
                        <Link to="/consumer/payments" className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaMoneyBillWave className="header-dropdown-icon" /> {t('header.paymentHistory')}
                        </Link>
                        <Link to="/consumer/reviews" className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaStar className="header-dropdown-icon" /> {t('header.myReviews')}
                        </Link>
                        <Link to="/reports" className="header-dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                          <FaExclamationTriangle className="header-dropdown-icon" /> {t('header.myReports')}
                        </Link>
                      </>
                    )}
                    <div className="header-dropdown-divider" />
                    <button className="header-dropdown-item header-dropdown-logout" onClick={handleLogout}>
                      <FaDoorOpen className="header-dropdown-icon" /> {t('common.logout')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="header-cart-btn" onClick={() => navigate('/cart')} aria-label="Shopping Cart">
                <HiOutlineShoppingCart className="header-cart-icon header-cart-icon-empty" />
              </button>
              <Link to="/login" className="header-btn-login">{t('common.login')}</Link>
              <Link to="/signup" className="header-btn-signup">{t('common.signup')}</Link>
            </>
          )}

          <button className="header-menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle Menu">
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;

